/**
 * TrapTycoonEngine.js
 * Handles the layout and routing of the main game interface.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { callBackend } from "../utils/api";
import { useNotify } from "./Notification";
import {
  playSound,
  toggleMusic,
  setSfxEnabled as setSfxUtils,
} from "../utils/sounds";

// Component Imports
import StatusBar from "./StatusBar";
import TrapManagement from "./TrapManagement";
import BlackMarket from "./BlackMarket";
import Leaderboard from "./Leaderboard";
import Arena from "./Arena";
import PlayerProfile from "./PlayerProfile";
import Bank from "./Bank";
import Shoutbox from "./Shoutbox";
import Hitlist from "./Hitlist";
import AllianceHQ from "./AllianceHQ";
import ContextMenu from "./ContextMenu";
import FieldManual from "./FieldManual";
import Changelog from "./Changelog";
import Settings from "./Settings";
import InventoryArmory from "./InventoryArmory";
import CityControl from "./CityControl";
import DrugOperations from "./DrugOperations";
import AchievementHall from "./AchievementHall";
import DirectChat from "./DirectChat";
import SocialManagement from "./SocialManagement";
import Mailbox from "./Mailbox";
import BusinessManager from "./BusinessManager";
import AdminPanel from "./AdminPanel";
import History from "./History";
import AllPlayers from "./AllPlayers";
import OnboardingOverlay from "./OnboardingOverlay";
import Hospital from "./Hospital";
import SurvivalManagement from "./SurvivalManagement";
import SevenEleven from "./SevenEleven";
import PremiumStore from "./PremiumStore";
import OfflineProgressModal from "./OfflineProgressModal";
import { log } from "../utils/logger";

import {
  Home,
  FlaskConical,
  Landmark,
  ShoppingCart,
  Swords,
  Map,
  Users,
  Trophy,
  Target,
  Book,
  History as HistoryIcon,
  Settings as SettingsIcon,
  MessageSquare,
  Shield,
  Package,
  Building2,
  ArrowLeft,
  Activity,
  Store,
  Gem,
} from "lucide-react";

export default function TrapTycoonEngine({
  stats,
  socket,
  fetchStats,
  returnSequence,
  returnIdleSeconds,
  onLogout,
  navigateTo,
}) {
  const { addNotification } = useNotify();

  const [recipes, setRecipes] = useState([]);
  const [activeCategory, setActiveCategory] = useState("HOME");
  const [activeTab, setActiveTab] = useState("EMPIRE");
  const [inspectingUser, setInspectingUser] = useState(null);
  const [isRequesting, setIsRequesting] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [requestCount, setRequestCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [returnPath, setReturnPath] = useState(null);
  const advancingTutorial = useRef(false);

  // Sound states with persistence
  const [musicEnabled, setMusicEnabled] = useState(
    localStorage.getItem("musicEnabled") === "true",
  );
  const [sfxEnabled, setSfxEnabled] = useState(
    localStorage.getItem("sfxEnabled") !== "false",
  );

  const fetchRequestCount = useCallback(async () => {
    if (!stats?.id) return;
    try {
      const data = await callBackend("/social/requests/count");
      setRequestCount(data.count);
    } catch (err) {
      log("TRAP_TYCOON_ENGINE", "Social count sync failed", err);
    }
  }, [stats?.id]);

  const fetchUnreadCount = useCallback(async () => {
    if (!stats?.id) return;
    try {
      const data = await callBackend("/social/messages/unread-count");
      setUnreadMessages(data.count);
    } catch (e) {
      log("TRAP_TYCOON_ENGINE", "Unread sync failed", e);
    }
  }, [stats?.id]);

  const fetchRecipes = useCallback(async () => {
    try {
      const data = await callBackend("/cookbook/fetch");
      setRecipes(data);
    } catch (err) {
      log("TRAP_TYCOON_ENGINE", "Failed to fetch recipes", err);
    }
  }, []);

  const handleToggleMusic = () => {
    const newState = !musicEnabled;
    setMusicEnabled(newState);
    localStorage.setItem("musicEnabled", newState);
    toggleMusic(newState);
  };

  const handleToggleSfx = () => {
    const newState = !sfxEnabled;
    setSfxEnabled(newState);
    localStorage.setItem("sfxEnabled", newState);
    setSfxUtils(newState);
  };

  const handleOpenChat = (friend) => {
    setSelectedFriend(friend);
    setInspectingUser(null);
    setReturnPath(null);
    setActiveCategory("PAGER");
    setActiveTab("MESSAGES");
  };

  const handleCloseChat = () => setSelectedFriend(null);

  const handleAcceptFriend = async (requesterId) => {
    try {
      const data = await callBackend("/social/accept", {
        method: "POST",
        body: { requesterId },
      });
      addNotification(data.message, "success");
      fetchRequestCount();
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleDenyFriend = async (requesterId) => {
    try {
      const data = await callBackend("/social/deny", {
        method: "POST",
        body: { requesterId },
      });
      addNotification(data.message, "info");
      fetchRequestCount();
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleClaimDaily = async () => {
    if (isRequesting) return false;
    setIsRequesting(true);
    try {
      const data = await callBackend("/claim-daily", { method: "POST" });
      addNotification(data.message, "success");
    } catch (err) {
      addNotification(err.message, "error");
      setIsRequesting(false);
      return false;
    }

    fetchStats();
    setIsRequesting(false);
    return true;
  };

  const handleFight = async () => {
    if (isRequesting) return;
    setIsRequesting(true);
    try {
      const data = await callBackend("/attack/pick-a-fight", {
        method: "POST",
        body: { clientStats: stats },
      });
      addNotification(
        `${data.message} ${data.isNPC ? "(AI RIVAL)" : ""}`,
        data.winner === "attacker" ? "success" : "error",
        "attack",
      );
      fetchStats();
    } catch (err) {
      addNotification(err.message || "No action tonight.", "error");
      log("TRAP_TYCOON_ENGINE", "Fight action failed", err);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleRepair = async (itemId) => {
    if (isRequesting) return;
    setIsRequesting(true);
    try {
      const data = await callBackend("/repair-item", {
        method: "POST",
        body: { itemId, clientStats: stats },
      });
      fetchStats();
      addNotification(data.message, "success");
    } catch (err) {
      addNotification(err.message || "Repair failed.", "error");
      log("TRAP_TYCOON_ENGINE", "Repair action failed", err);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleRepairAll = async () => {
    if (isRequesting) return;
    setIsRequesting(true);
    try {
      const data = await callBackend("/repair-all", { method: "POST" });
      fetchStats();
      addNotification(data.message, "success");
    } catch (err) {
      addNotification(err.message, "error");
      log("TRAP_TYCOON_ENGINE", "Repair all action failed", err);
    } finally {
      setIsRequesting(false);
    }
  };

  // Scouting handlers
  const handleScout = async (count = 1) =>
    handleGenericScout("/scout-runners", count);
  const handleScoutGirls = async (count = 1) =>
    handleGenericScout("/scout-girls", count);
  const handleScoutThugs = async (count = 1) =>
    handleGenericScout("/scout-thugs", count);
  const handleScoutCooks = async (count = 1) =>
    handleGenericScout("/scout-cooks", count);

  const handleGenericScout = async (endpoint, count = 1) => {
    if (isRequesting) return;
    setIsRequesting(true);
    try {
      const data = await callBackend(endpoint, {
        method: "POST",
        body: {
          clientStats: stats,
          count: count,
        },
      });
      addNotification(data.message, "success");
      fetchStats();
    } catch (err) {
      addNotification(err.message || "Spotters came back empty.", "error");
      log("TRAP_TYCOON_ENGINE", "Scout action failed", err);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleTargetAttack = async (targetId) => {
    if (isRequesting) return;
    setIsRequesting(true);
    try {
      const data = await callBackend(`/attack/${targetId}`, {
        method: "POST",
        body: { clientStats: stats },
      });
      addNotification(data.message, "success", "attack");
      fetchStats();
    } catch (err) {
      addNotification(err.message || "Operation failed.", "error");
      log("TRAP_TYCOON_ENGINE", "Target attack action failed", err);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleUnlock = async (recipeId) => {
    if (isRequesting) return;
    setIsRequesting(true);
    try {
      const data = await callBackend("/cookbook/unlock", {
        method: "POST",
        body: { recipeId, clientStats: stats },
      });
      await fetchRecipes();
      await fetchStats();
      addNotification(data.message, "success");
    } catch (err) {
      addNotification(err.message, "error");
      log("TRAP_TYCOON_ENGINE", "Unlock action failed", err);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleCook = async (recipeId) => {
    if (isRequesting) return;
    setIsRequesting(true);
    try {
      const data = await callBackend("/cookbook/produce", {
        method: "POST",
        body: { recipeId, clientStats: stats },
      });
      addNotification(data.message, "success");
      fetchStats();
    } catch (err) {
      addNotification(err.message, "error");
      log("TRAP_TYCOON_ENGINE", "Cook action failed", err);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleSell = async (recipeId, amount) => {
    if (isRequesting) return;
    setIsRequesting(true);
    try {
      const data = await callBackend("/sell-product", {
        method: "POST",
        body: { recipeId, amount, clientStats: stats },
      });
      if (!data.busted) playSound("cash");
      addNotification(data.message, "success");
      setTimeout(() => fetchStats(), 500);
    } catch (err) {
      addNotification(err.message || "Stash is locked.", "error");
      log("TRAP_TYCOON_ENGINE", "Sell action failed", err);
    } finally {
      setIsRequesting(false);
    }
  };

  const handlePostBounty = async (targetId, amount, reason) => {
    if (isRequesting) return;
    setIsRequesting(true);
    try {
      const data = await callBackend("/hitlist/add", {
        method: "POST",
        body: { targetId, amount, reason, clientStats: stats },
      });
      addNotification(data.message, "success");
      fetchStats();
    } catch (err) {
      addNotification(err.message, "error");
      log("TRAP_TYCOON_ENGINE", "Post bounty action failed", err);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleInspectById = async (userId) => {
    if (isRequesting) return;
    setIsRequesting(true);
    try {
      const user = await callBackend(`/profile/${userId}`);

      // Only set the return path if we are NOT already inspecting someone
      // This prevents losing the original tab if you click profile -> profile
      if (!inspectingUser) {
        setReturnPath({ category: activeCategory, tab: activeTab });
      }

      setInspectingUser(user);
      // Ensure we don't switch tabs, just show the profile overlay
    } catch (err) {
      addNotification("Dawg went ghost.", "error");
      log("TRAP_TYCOON_ENGINE", "Inspect by ID action failed", err);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleCloseProfile = () => {
    setInspectingUser(null);
    if (returnPath) {
      setActiveCategory(returnPath.category);
      setActiveTab(returnPath.tab);
      setReturnPath(null);
    }
  };

  const handleAssignCook = async (inventoryId, recipeId) => {
    try {
      const data = await callBackend("/cooks/assign", {
        method: "POST",
        body: { inventoryId, recipeId: recipeId ? parseInt(recipeId) : null },
      });
      addNotification(data.message, "success");
      fetchStats();
    } catch (err) {
      addNotification(err.message, "error");
      log("TRAP_TYCOON_ENGINE", "Assign cook action failed", err);
    }
  };

  const handleAssignRunner = async (inventoryId, recipeId) => {
    try {
      const data = await callBackend("/runners/assign", {
        method: "POST",
        body: { inventoryId, recipeId: recipeId ? parseInt(recipeId) : null },
      });
      addNotification(data.message, "success");
      fetchStats();
    } catch (err) {
      addNotification(err.message, "error");
      log("TRAP_TYCOON_ENGINE", "Assign runner action failed", err);
    }
  };

  const handleAdvanceTutorial = useCallback(async () => {
    if (advancingTutorial.current) return;
    advancingTutorial.current = true;
    try {
      await callBackend("/system/tutorial-next", { method: "POST" });
      await fetchStats();
    } catch (e) {
      log("ONBOARDING", "Failed to advance tutorial", e);
    } finally {
      advancingTutorial.current = false;
    }
  }, [fetchStats]);

  const handleEquipUnit = async (mercenaryInvId, itemInvId, slot) => {
    if (isRequesting) return;
    setIsRequesting(true);
    try {
      const data = await callBackend("/mercenaries/equip", {
        method: "POST",
        body: { mercenaryInvId, itemInvId, slot },
      });
      addNotification(data.message, "success");
      fetchStats(); // Refresh inventory to show new equipment
    } catch (err) {
      addNotification(err.message || "Failed to update loadout.", "error");
    } finally {
      setIsRequesting(false);
    }
  };

  const handleConsume = async (invId) => {
    if (isRequesting) return;
    setIsRequesting(true);
    try {
      const data = await callBackend("/items/consume", {
        method: "POST",
        body: { inventoryId: invId },
      });
      addNotification(data.message || "Item consumed", "success");
      fetchStats();
    } catch (err) {
      addNotification(err.message || "Failed to use item", "error");
      log("TRAP_TYCOON_ENGINE", "Consume action failed", err);
    } finally {
      setIsRequesting(false);
    }
  };

  // Logic to auto-advance steps based on user actions
  useEffect(() => {
    const shouldAdvance =
      (stats.tutorial_step === 1 && stats.experience > 0) ||
      (stats.tutorial_step === 2 && stats.thugs > 0) ||
      (stats.tutorial_step === 3 && stats.stash?.length > 0) ||
      (stats.tutorial_step === 4 &&
        stats.stash?.some((stash) => stash.quantity > 0)) ||
      (stats.tutorial_step === 5 && stats.cash_earned > 500) ||
      (stats.tutorial_step === 6 && stats.runners > 0);

    if (shouldAdvance) handleAdvanceTutorial();
  }, [handleAdvanceTutorial, stats]);

  // Load Game Data (Runs on mount and when fetch functions change)
  useEffect(() => {
    fetchRecipes();
    fetchRequestCount();
    fetchUnreadCount();
  }, [fetchRecipes, fetchRequestCount, fetchUnreadCount]);

  // Initialize and Sync Sound Settings (Runs only when sound states change)
  useEffect(() => {
    toggleMusic(musicEnabled);
    setSfxUtils(sfxEnabled);
  }, [musicEnabled, sfxEnabled]);

  // Socket Listener for incoming attacks
  useEffect(() => {
    if (stats?.id) {
      const handler = (data) => {
        addNotification(data.message, data.type === "raid" ? "error" : "info");
        fetchStats();
      };
      socket.on(`attacked_${stats.id}`, handler);
      return () => socket.off(`attacked_${stats.id}`, handler);
    }
  }, [stats?.id, socket, fetchStats, addNotification]);

  const NAV_STRUCTURE = {
    // HOME Tabs
    HOME: [
      "EMPIRE",
      "SURVIVAL",
      "SAFE",
      "ITEMS",
      "ACHIEVEMENTS",
    ],
    // Private communications
    PAGER: ["MESSAGES", "FRIENDS"],
    // TRADER CITY Tabs
    "TRADER CITY": ["BLACK MARKET", "SEVEN ELEVEN", "PREMIUM STORE"],
    // SYNDICATE Tabs
    SYNDICATE: [
      "CITY",
      "OFFICE",
      "OPERATIONS",
      "FIGHTING",
      "TARGETS",
      "LEADERBOARD",
      "ALLIANCE",
      "HISTORY",
    ],
    // WHITEPAGES
    WHITEPAGES: [],
    //
    HOSPITAL: [],
    // SYSTEM Tabs
    SYSTEM: [
      "FIELD MANUAL",
      "CHANGELOG",
      "SETTINGS",
      ...(stats?.role !== "user" ? ["STAFF"] : []),
    ],
  };

  const TAB_ICONS = {
    // HOME Tabs
    EMPIRE: <Home size={14} className="text-green-500" />,
    SURVIVAL: <Activity size={16} />,
    ITEMS: <Package size={14} className="text-green-500" />,
    FRIENDS: (
      <div className="relative">
        <Users size={14} className="text-blue-500" />
        {requestCount > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-600 text-[8px] px-1 rounded-full">
            {requestCount}
          </span>
        )}
      </div>
    ),
    MESSAGES: (
      <div className="relative">
        <MessageSquare size={14} className="text-cyan-500" />
        {unreadMessages > 0 && (
          <span className="absolute -top-2 -right-2 bg-blue-600 text-black text-[7px] font-black px-1 rounded-full border border-black">
            {unreadMessages}
          </span>
        )}
      </div>
    ),
    ACHIEVEMENTS: <Trophy size={14} className="text-yellow-500" />,
    SAFE: <Landmark size={14} className="text-green-500" />,
    // TRADER CITY Tabs
    "BLACK MARKET": <ShoppingCart size={14} className="text-purple-500" />,
    "SEVEN ELEVEN": <Store size={16} className="text-yellow-500" />,
    "PREMIUM STORE": <Gem size={14} className="text-indigo-500" />,
    // SYNDICATE Tabs
    CITY: <Map size={14} className="text-purple-500" />,
    OFFICE: <Building2 size={14} className="text-purple-500" />,
    OPERATIONS: <FlaskConical size={14} className="text-blue-500" />,
    FIGHTING: <Swords size={14} className="text-red-500" />,
    TARGETS: <Target size={14} className="text-red-500" />,
    LEADERBOARD: <Trophy size={14} className="text-yellow-500" />,
    ALLIANCE: <Users size={14} className="text-blue-500" />,
    HISTORY: <HistoryIcon size={14} className="text-blue-500" />,
    // SYSTEM TABS
    "FIELD MANUAL": <Book size={14} className="text-green-500" />,
    CHANGELOG: <HistoryIcon size={14} className="text-orange-500" />,
    SETTINGS: <SettingsIcon size={14} className="text-red-500" />,
    STAFF: <Shield size={14} className="text-pink-500" />,
  };

  return (
    <>
      <OnboardingOverlay stats={stats} onNext={handleAdvanceTutorial} />
      <OfflineProgressModal
        stats={stats}
        returnSequence={returnSequence}
        returnIdleSeconds={returnIdleSeconds}
        onClaimDaily={handleClaimDaily}
        isRequesting={isRequesting}
      />
      <ContextMenu
        onAction={(action) => {
          switch (action) {
            case "LEADERBOARD":
            case "LOGS":
              setActiveCategory("SYNDICATE");
              setActiveTab("HISTORY");
              setInspectingUser(null);
              setReturnPath(null);
              break;
            case "ITEMS":
            case "SHOUTBOX":
              setActiveCategory("HOME");
              setActiveTab("EMPIRE");
              setInspectingUser(null);
              setReturnPath(null);
              break;
            case "FIELD_MANUAL":
              setActiveCategory("SYSTEM");
              setActiveTab("FIELD MANUAL");
              setInspectingUser(null);
              setReturnPath(null);
              break;
            case "LOGOUT":
              onLogout();
              break;
            default:
              break;
          }
        }}
      />
      <StatusBar
        stats={stats}
        onClaimDaily={handleClaimDaily}
        onTimerComplete={fetchStats}
      />

      <div className="p-6 max-w-6xl mx-auto">
        {/* Navigation Locks: If NOT ALIVE, hide nav bars */}
        {stats.state === "alive" && (
          <>
            {/* Desktop Navigation */}
            <nav className="hidden md:block space-y-4 mb-8">
              <div className="flex justify-center gap-2 border-b border-zinc-900 pb-4 overflow-x-auto scrollbar-hide">
                {Object.keys(NAV_STRUCTURE).map((cat) => (
                  <button
                    key={cat}
                    onClick={() => {
                      setInspectingUser(null);
                      setReturnPath(null);
                      setActiveCategory(cat);
                      setActiveTab(NAV_STRUCTURE[cat][0]);
                    }}
                    className={`px-8 py-3 text-sm font-black italic transition-all border-b-2 whitespace-nowrap ${activeCategory === cat ? "text-white border-green-500 bg-green-900/10" : "text-zinc-600 border-transparent hover:text-zinc-400"}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap justify-center gap-3">
                {NAV_STRUCTURE[activeCategory].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => {
                      setInspectingUser(null);
                      setReturnPath(null);
                      setActiveTab(tab);
                    }}
                    className={`px-4 py-2 text-[10px] font-black uppercase italic border flex items-center gap-2 transition-all ${activeTab === tab ? "bg-zinc-100 text-black border-white" : "text-zinc-500 border-zinc-800 hover:text-white"}`}
                  >
                    {TAB_ICONS[tab]} {tab}
                  </button>
                ))}
              </div>
            </nav>

            {/* Mobile Navigation */}
            <nav className="md:hidden fixed bottom-0 left-0 right-0 z-[5000] bg-zinc-950 border-t-2 border-green-900 px-2 pb-safe">
              <div className="flex justify-around border-b border-zinc-900/50">
                {Object.keys(NAV_STRUCTURE).map((cat) => (
                  <button
                    key={cat}
                    onClick={() => {
                      setActiveCategory(cat);
                      setActiveTab(NAV_STRUCTURE[cat][0]);
                    }}
                    className={`py-2 text-[10px] font-black italic transition-all uppercase ${activeCategory === cat ? "text-green-500" : "text-zinc-600"}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              <div className="flex justify-center gap-4 py-3 overflow-x-auto scrollbar-hide px-2">
                {NAV_STRUCTURE[activeCategory].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => {
                      setInspectingUser(null);
                      setReturnPath(null);
                      setActiveTab(tab);
                    }}
                    className={`flex flex-col items-center gap-1 min-w-[80px] transition-all ${activeTab === tab ? "text-white scale-110" : "text-zinc-600"}`}
                  >
                    <span className={activeTab === tab ? "text-green-500" : ""}>
                      {TAB_ICONS[tab]}
                    </span>
                    <span className="text-[7px] font-bold uppercase whitespace-nowrap">
                      {tab}
                    </span>
                  </button>
                ))}
              </div>
            </nav>
          </>
        )}

        <main className={stats.state === "alive" ? "pb-28 md:pb-0" : ""}>
          {/* HOSPITAL LOCK: Force hospital view if incapacitated */}
          {stats.state !== "alive" ? (
            <div className="animate-in zoom-in-95 duration-500">
              <Hospital user={stats} fetchStats={fetchStats} />
            </div>
          ) : inspectingUser ? (
            <div className="animate-in fade-in slide-in-from-right duration-500">
              <button
                onClick={handleCloseProfile}
                className="mb-4 flex items-center gap-2 text-[10px] font-black uppercase text-zinc-500 hover:text-white transition-colors border border-zinc-800 px-3 py-1.5 bg-black"
              >
                <ArrowLeft size={12} /> Return to{" "}
                {returnPath?.tab || "Previous Page"}
              </button>
              <PlayerProfile
                user={stats}
                targetUser={inspectingUser}
                onAttack={handleTargetAttack}
                onPostBounty={handlePostBounty}
                onOpenChat={handleOpenChat}
              />
            </div>
          ) : (
            <>
              {/* HOME TABS */}
              {activeTab === "EMPIRE" && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-in slide-in-from-left fade-in duration-700">
                  <div className="lg:col-span-2 space-y-8 h-full">
                    <TrapManagement
                      stats={stats}
                      onScout={handleScout}
                      onScoutGirls={handleScoutGirls}
                      onScoutThugs={handleScoutThugs}
                      onScoutCooks={handleScoutCooks}
                      onSell={handleSell}
                      onEquip={handleEquipUnit}
                      fetchStats={fetchStats}
                    />
                  </div>
                  <div className="space-y-8">
                    <Shoutbox
                      socket={socket}
                      stats={stats}
                      onInspect={handleInspectById}
                    />
                  </div>
                </div>
              )}
              {activeTab === "SURVIVAL" && (
                <SurvivalManagement
                  stats={stats}
                  fetchStats={fetchStats} // Added missing fetchStats prop
                />
              )}
              {activeTab === "ITEMS" && (
                <div className="space-y-6">
                  <div className="border-b border-zinc-800 pb-4">
                    <h2 className="text-3xl font-black italic uppercase text-white">
                      The Stash
                    </h2>
                    <p className="text-zinc-500 text-xs uppercase tracking-widest">
                      Global Inventory Management
                    </p>
                  </div>
                  <InventoryArmory
                    inventory={stats.inventory}
                    onRepair={handleRepair}
                    onRepairAll={handleRepairAll}
                    onConsume={handleConsume}
                  />
                </div>
              )}
              {activeTab === "SAFE" && (
                <Bank
                  stats={stats}
                  fetchStats={fetchStats}
                  onTransactionSuccess={fetchStats}
                />
              )}
              {activeTab === "FRIENDS" && (
                <SocialManagement
                  stats={stats}
                  onAcceptFriend={handleAcceptFriend}
                  onDenyFriend={handleDenyFriend}
                  onOpenChat={handleOpenChat}
                  onUpdateRequests={fetchRequestCount}
                  onInspect={handleInspectById}
                />
              )}
              {activeTab === "MESSAGES" &&
                (selectedFriend ? (
                  <DirectChat
                    friend={selectedFriend}
                    onClose={() => {
                      handleCloseChat();
                      fetchUnreadCount();
                    }}
                    currentUser={stats}
                    socket={socket}
                  />
                ) : (
                  <Mailbox
                    onOpenChat={(friend) => setSelectedFriend(friend)}
                    onComposeNew={() => setActiveTab("FRIENDS")}
                  />
                ))}
              {activeTab === "ACHIEVEMENTS" && <AchievementHall />}

              {/* TRADER CITY TABS */}
              {activeTab === "BLACK MARKET" && (
                <BlackMarket
                  userCash={stats.cash}
                  userLevel={stats.level}
                  onPurchaseSuccess={fetchStats}
                />
              )}

              {activeTab === "SEVEN ELEVEN" && (
                <SevenEleven stats={stats} onPurchaseSuccess={fetchStats} />
              )}

              {activeTab === "PREMIUM STORE" && (
                <PremiumStore stats={stats} fetchStats={fetchStats} />
              )}

              {/* SYNDICATE TABS */}
              {activeTab === "CITY" && (
                <CityControl userStats={stats} onActionSuccess={fetchStats} />
              )}
              {activeTab === "OFFICE" && (
                <BusinessManager
                  userStats={stats}
                  onActionSuccess={fetchStats}
                />
              )}
              {activeTab === "OPERATIONS" && (
                <DrugOperations
                  stats={stats}
                  inventory={stats.inventory}
                  recipes={recipes}
                  stash={stats.stash}
                  allianceTag={stats.alliance_tag}
                  onAssignCook={handleAssignCook}
                  onAssignRunner={handleAssignRunner}
                  onSell={handleSell}
                  onUnlock={handleUnlock}
                  onCook={handleCook}
                />
              )}
              {activeTab === "FIGHTING" && <Arena onFight={handleFight} />}
              {activeTab === "TARGETS" && (
                <Hitlist onInspect={handleInspectById} />
              )}
              {activeTab === "LEADERBOARD" &&
                (inspectingUser ? (
                  <div className="animate-in fade-in slide-in-from-right duration-500">
                    <button
                      onClick={handleCloseProfile}
                      className="mb-4 flex items-center gap-2 text-[10px] font-black uppercase text-zinc-500 hover:text-white transition-colors border border-zinc-800 px-3 py-1.5 bg-black"
                    >
                      <ArrowLeft size={12} /> Return to{" "}
                      {returnPath?.tab || "Previous Page"}
                    </button>
                    <PlayerProfile
                      user={stats}
                      targetUser={inspectingUser}
                      onAttack={handleTargetAttack}
                      onPostBounty={handlePostBounty}
                      onOpenChat={handleOpenChat}
                    />
                  </div>
                ) : (
                  <Leaderboard
                    onInspect={(player) => handleInspectById(player.id)}
                  />
                ))}
              {activeTab === "ALLIANCE" && (
                <AllianceHQ stats={stats} onUpdate={fetchStats} />
              )}
              {activeTab === "HISTORY" && <History />}

              {/* WHITEPAGES TABS */}
              {activeCategory === "WHITEPAGES" &&
                (inspectingUser ? (
                  <div className="animate-in fade-in slide-in-from-right duration-500">
                    <button
                      onClick={handleCloseProfile}
                      className="mb-4 flex items-center gap-2 text-[10px] font-black uppercase text-zinc-500 hover:text-white transition-colors border border-zinc-800 px-3 py-1.5 bg-black"
                    >
                      <ArrowLeft size={12} /> Return to{" "}
                      {returnPath?.tab || "Previous Page"}
                    </button>
                    <PlayerProfile
                      user={stats}
                      targetUser={inspectingUser}
                      onAttack={handleTargetAttack}
                      onPostBounty={handlePostBounty}
                      onOpenChat={handleOpenChat}
                    />
                  </div>
                ) : (
                  <AllPlayers
                    onInspect={(userId) => handleInspectById(userId)}
                  />
                ))}

              {/* HOSPITAL TABS */}
              {activeCategory === "HOSPITAL" && (
                <Hospital user={stats} fetchStats={fetchStats} />
              )}

              {/* SYSTEM TABS */}
              {activeTab === "FIELD MANUAL" && (
                <FieldManual stats={stats} recipes={recipes} />
              )}
              {activeTab === "CHANGELOG" && <Changelog />}
              {activeTab === "SETTINGS" && (
                <Settings
                  onLogout={onLogout}
                  musicEnabled={musicEnabled}
                  onToggleMusic={handleToggleMusic}
                  sfxEnabled={sfxEnabled}
                  onToggleSfx={handleToggleSfx}
                />
              )}
              {activeTab === "STAFF" && stats?.role !== "user" && (
                <AdminPanel role={stats.role} />
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
}
